'use client';

/**
 * @fileoverview `useHostTransition` — drives the one-shot `LayerTransition`
 * enter animation when the user has just crossed LOCI hosts (WEB-473, slice of
 * WEB-463).
 *
 * Host switches (Apex ⇄ City ⇄ App ⇄ Studio) are cross-host **hard
 * navigations** — different subdomains, full document unload/reload — so there
 * is no App Router event to observe and no opportunity to play an *exit*
 * animation without intercepting the native cross-host `<a>` (which the routing
 * rules require stays a plain anchor). Instead the freshly-loaded destination
 * host plays an **enter** transition: on mount we classify `document.referrer`,
 * and if it is a *different* recognised LOCI host than the current one, we
 * surface the `from` → `to` nodes for a single one-shot crossfade.
 *
 * This is purely additive: the Wayfinder (and any other cross-host link) keeps
 * its native `<a href>` and navigates exactly as before — the transition is a
 * read-only reaction to having arrived, never a gate on the navigation itself.
 *
 * Once-per-document guard: `document.referrer` is set at document load and is
 * **immutable** for the document's lifetime — a soft (App Router) navigation
 * never updates it. App and Studio share the `app.loci.city` host, so crossing
 * App ⇄ Studio is a *same-host soft nav* that remounts the shell (different
 * layout segment) but keeps the same document — and therefore the same, now
 * stale, referrer. Without a guard that would (a) replay the original
 * cross-host transition on every shell remount and (b) mis-fire a city→studio
 * transition when you actually came from the app. We therefore latch a
 * `window`-level flag the first time the transition activates: it survives shell
 * remounts (same window) and resets on a real cross-host hard navigation (new
 * document), so each arrival plays exactly one enter transition and never
 * replays. (App ⇄ Studio's *own* layer change is a soft nav the referrer cannot
 * observe — covering it would need a router-event approach like
 * `useRouteProgress`; out of scope for this referrer-driven slice.)
 *
 * SSR-safe: starts inactive on the server and on first client paint (the mount
 * renders `null`), so server/client markup matches — no hydration mismatch. All
 * `window` / `document` / `matchMedia` access happens inside the effect.
 *
 * Reduced motion: when the user prefers reduced motion the hook never
 * activates, so the overlay never mounts (no flash). The CSS keyframes are also
 * centrally gated in `loci-motion.css` as a second line of defence.
 */

import { useEffect, useState } from 'react';
import type { CityCode } from '@loci/city-editions';

import type { LayerNode } from '@/components/atoms/motion/LayerTransition';
import { type HostLayer, layerFromHostname, layerNode } from '@/lib/host-layer';

export interface HostTransitionState {
  /** Outgoing host node (where the referrer says you came from). */
  readonly from: LayerNode;
  /** Incoming host node (the surface that just mounted). */
  readonly to: LayerNode;
}

export interface UseHostTransitionOptions {
  /**
   * The layer of the surface mounting the transition. Supplied explicitly by
   * each shell (apex / city / app / studio) — App and Studio share the
   * `app.loci.city` host and cannot be told apart from a hostname alone.
   */
  currentLayer: HostLayer;
  /** Resolved city code, used to specialise city/app/studio sub-lines. */
  cityCode?: CityCode;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Window-scoped latch so the enter transition plays at most once per document.
 * Lives on `window` (not a module variable) so it is unambiguously reset by a
 * fresh document on hard navigation, and shared across the separate shell mounts
 * that may remount within one document via soft nav.
 */
const PLAYED_FLAG = '__lociHostTransitionPlayed' as const;

interface LociTransitionWindow {
  [PLAYED_FLAG]?: boolean;
}

function hasPlayedThisDocument(): boolean {
  return (window as unknown as LociTransitionWindow)[PLAYED_FLAG] === true;
}

function markPlayedThisDocument(): void {
  (window as unknown as LociTransitionWindow)[PLAYED_FLAG] = true;
}

/**
 * Returns the `{ from, to }` nodes for a one-shot enter transition when the
 * current document was reached by crossing from a *different* LOCI host, or
 * `null` otherwise (same-host reload, in-app SPA nav, external/direct entry, or
 * reduced motion). Computed once on mount.
 */
export function useHostTransition({
  currentLayer,
  cityCode,
}: UseHostTransitionOptions): HostTransitionState | null {
  const [state, setState] = useState<HostTransitionState | null>(null);

  useEffect(() => {
    if (prefersReducedMotion()) return;

    // Already played for this document (e.g. a soft-nav shell remount that
    // re-read the same immutable referrer) — never replay.
    if (hasPlayedThisDocument()) return;

    const referrer = document.referrer;
    if (!referrer) return; // direct entry / no referrer — nothing to transition from.

    let fromLayer: ReturnType<typeof layerFromHostname>;
    let sameHost: boolean;
    try {
      const fromUrl = new URL(referrer);
      sameHost = fromUrl.host === window.location.host;
      fromLayer = layerFromHostname(fromUrl.host);
    } catch {
      return; // unparseable referrer — bail rather than guess.
    }

    // Same host (in-page reload, or an in-app nav that left a same-host
    // referrer) is not a host switch — the app-shell RouteProgress filament
    // already covers same-host transitions.
    if (sameHost) return;

    // Referrer is not a recognised LOCI host (came from elsewhere on the web):
    // we don't know the origin layer, so don't fabricate a transition.
    if (fromLayer === null) return;

    // No-op if the referrer classifies to the same layer we're already on
    // (e.g. app → studio both resolve to `app` by hostname; the path changed
    // but the layer didn't, so there's nothing to announce).
    if (fromLayer === currentLayer) return;

    // Latch BEFORE rendering so a sibling/soft-nav remount within this document
    // can't replay it.
    markPlayedThisDocument();
    setState({
      from: layerNode(fromLayer),
      to: layerNode(currentLayer, cityCode),
    });
    // Intentionally mount-only: a cross-host switch is a fresh document load, so
    // this effect runs exactly once for the lifetime of this host's page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}
